import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import { FC } from 'react';

/**
 * Mirrors `LessonDetailView` (default template) so the page doesn't jump when
 * the lesson arrives: the sticky nav row, the description card beside the
 * "In this lesson" summary + progress cards, then the Video/s strip and the
 * Documents panel separated by the same dividers. Widths, radii and paddings
 * are copied from the real components.
 */

// Top row: description card 631 + gap 24 + summary column 368.
const ROW_MAX_WIDTH = 1023;
const DESCRIPTION_CARD_WIDTH = 631;
const SUMMARY_COLUMN_WIDTH = 368;
const CARD_RADIUS = '15px';

// Unique per line so they double as React keys.
const DESCRIPTION_LINE_WIDTHS = ['100%', '97%', '94%', '98%', '90%', '96%', '93%', '60%'];
const SUMMARY_ROW_WIDTHS = ['55%', '70%', '62%', '66%'];
const DOCUMENT_CARD_COUNT = 2;

const rowWidthSx = { width: '100%', maxWidth: { md: ROW_MAX_WIDTH } };

export const LessonDetailSkeleton: FC = () => (
  <Box>
    {/* Sticky nav row — breadcrumb trail on desktop, back link on mobile */}
    <Box sx={{ py: { xs: 1, md: 1.5 }, mb: { xs: 1, md: 1.5 } }}>
      <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, minHeight: 28 }}>
        <Skeleton variant="circular" width={20} height={20} />
        <Skeleton variant="text" width={120} sx={{ fontSize: 14, lineHeight: '20px' }} />
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
        <Skeleton variant="text" width={130} sx={{ fontSize: 14, lineHeight: '22px' }} />
        <Skeleton variant="text" width={8} sx={{ fontSize: 14, lineHeight: '22px' }} />
        <Skeleton variant="text" width={60} sx={{ fontSize: 14, lineHeight: '22px' }} />
        <Skeleton variant="text" width={8} sx={{ fontSize: 14, lineHeight: '22px' }} />
        <Skeleton variant="text" width={260} sx={{ fontSize: 14, lineHeight: '22px' }} />
      </Box>
    </Box>

    <Box sx={{ maxWidth: { xs: '100%', md: ROW_MAX_WIDTH } }}>
      {/* Top row — description card left, summary + progress cards right */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Box
          sx={{
            flex: { xs: '1 1 100%', md: `1 1 ${DESCRIPTION_CARD_WIDTH}px` },
            maxWidth: { xs: '100%', md: DESCRIPTION_CARD_WIDTH },
            minWidth: 0,
            bgcolor: 'white',
            borderRadius: CARD_RADIUS,
            p: 2,
          }}
        >
          <Skeleton variant="text" width="65%" sx={{ fontSize: 16, lineHeight: '24px', mb: 1.5 }} />
          {DESCRIPTION_LINE_WIDTHS.map((width, i) => (
            <Skeleton
              key={width}
              variant="text"
              width={width}
              sx={{
                fontSize: 14,
                lineHeight: '22.4px',
                // On mobile the description is clamped to 5 lines behind "Read More".
                display: { xs: i < 5 ? 'block' : 'none', md: 'block' },
              }}
            />
          ))}
          <Skeleton
            variant="text"
            width={110}
            sx={{ fontSize: 14, lineHeight: '36px', mt: 1, display: { md: 'none' } }}
          />
        </Box>

        <Box
          sx={{
            flex: { xs: '1 1 100%', md: `0 0 ${SUMMARY_COLUMN_WIDTH}px` },
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {/* "In this lesson" summary card */}
          <Box sx={{ bgcolor: 'white', borderRadius: CARD_RADIUS, border: '1px solid #f8e8e4', p: 2 }}>
            <Skeleton variant="text" width={120} sx={{ fontSize: 12, lineHeight: '20px', mb: 1 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {SUMMARY_ROW_WIDTHS.map(width => (
                <Box key={width} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Skeleton variant="circular" width={12} height={12} sx={{ flexShrink: 0 }} />
                  <Skeleton variant="text" width={width} sx={{ fontSize: 12, lineHeight: '17px' }} />
                </Box>
              ))}
            </Box>
          </Box>

          {/* Progress card */}
          <Skeleton variant="rectangular" height={66} sx={{ borderRadius: CARD_RADIUS }} />
        </Box>
      </Box>

      <Divider sx={{ my: 4, ...rowWidthSx }} />

      {/* Video/s strip */}
      <Box sx={rowWidthSx}>
        <Skeleton variant="text" width={70} sx={{ fontSize: 16, lineHeight: '24px', mb: 1.5 }} />
        <Box sx={{ py: 1, display: 'flex', gap: 3 }}>
          <Box
            sx={{
              width: 288,
              maxWidth: '100%',
              flexShrink: 0,
              bgcolor: 'white',
              borderRadius: CARD_RADIUS,
              boxShadow: '0px 5px 10px rgba(0,0,0,0.04)',
            }}
          >
            <Box sx={{ p: 1.5 }}>
              <Skeleton variant="rectangular" height={149} sx={{ borderRadius: '13px' }} />
            </Box>
            <Box sx={{ px: 2, pb: 2 }}>
              <Skeleton variant="text" width="55%" sx={{ fontSize: 14, lineHeight: '20px', mb: 0.5 }} />
              <Skeleton variant="text" width={40} sx={{ fontSize: 12, lineHeight: '18px' }} />
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 4, ...rowWidthSx }} />

      {/* Documents panel */}
      <Box sx={{ bgcolor: 'white', borderRadius: CARD_RADIUS, p: 3, ...rowWidthSx }}>
        <Skeleton variant="text" width={110} sx={{ fontSize: 16, lineHeight: '24px', mb: 1.5 }} />
        <Box sx={{ display: 'flex', gap: 2.25, flexWrap: 'wrap' }}>
          {Array.from({ length: DOCUMENT_CARD_COUNT }).map((_, i) => (
            // oxlint-disable-next-line react/no-array-index-key
            <Box key={i} sx={{ width: 107, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
              <Skeleton variant="rectangular" width={53} height={65} sx={{ borderRadius: 1 }} />
              <Skeleton variant="text" width={80} sx={{ fontSize: 12, lineHeight: '16px' }} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  </Box>
);
